import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { Grid, Tooltip } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { OptionType } from '../../utils/type';
import { ConsentClientContact } from '../../services/configApi/forms/Consent/ConsentServices';
import Textinput from '../../components/formlib/Textinput';
import { nameValidation } from '../../utils/formValidationUtil';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';

interface ContactDetails {
  contacts: ConsentClientContact[] | [];
  titleLabel: string;
  defaultTitleLabel: string;
  searchContactLabel: string;
  defaultSearchContactLabel: string;
  emailLabelId: string;
  emailDefaultLabelId: string;
  addressLabelId: string;
  addressDefaultLabelId: string;
  phoneLabelId: string;
  phoneDefaultLabelId: string;
  contactOtherName: string;
  dropDownValidation: boolean;
  dropDownOtherContactValidationState: boolean;
  contactData: OptionType[];
  screenLocked: boolean;
  onSelect: (value: OptionType[] | null) => void;
  handleContactOtherName: (value: string) => void;
}

const ConsentSspAgencyDetails: FC<ContactDetails> = (props) => {
  const [selectAgency, setSelectAgency] = useState<OptionType[]>(
    props.contacts.map((contact) => {
      if (contact.isGuardian) {
        const labels = [
          contact.isPrimary && 'Primary',
          contact.isLegalGuardian && 'Legal',
        ].filter(Boolean);
        return {
          id: contact.id.toString(),
          label: contact.name
            ? `${contact.name}${
                labels.length > 0 ? ` - (${labels.join(', ')})` : ''
              }`
            : contact.name,
        };
      }
      return {
        id: contact.id.toString(),
        label: contact.name || '',
      };
    }),
  );
  const [selectedContact, setSelectedContact] = useState<
    ConsentClientContact[]
  >([]);

  useEffect(() => {
    const handleSelected = async () => {
      if (props.contacts.length > 0) {
        const selectedContactList = props.contacts.filter((v) => {
          return selectAgency.some((item) => item.id === v.id.toString());
        });
        if (selectedContactList) {
          setSelectedContact(selectedContactList);
        } else {
          setSelectedContact([]);
          setSelectAgency([]);
        }
      }
    };

    handleSelected();
  }, [props.contacts]);

  const handleDropdownChange = (value: OptionType[]) => {
    if (value.length > 0) {
      const selectedContactList = props.contacts.filter((v) => {
        return value.some((item) => item.id === v.id.toString());
      });
      setSelectAgency(value);
      setSelectedContact(selectedContactList);
      props.onSelect(value);
    } else {
      setSelectAgency([]);
      setSelectedContact([]);
      props.onSelect([]);
    }
  };

  const renderContactField = (
    labelId: string,
    defaultLabel: string,
    value: string,
    className?: string,
  ) => (
    <Box className={`flex__ ${className ?? 'pr-md'}`}>
      <MediumTypography
        labelid={labelId}
        defaultlabel={defaultLabel}
        fontSize="16px"
        textColor="#97A6A5"
      />
      <Box className="width200px">
        <Tooltip
          title={value && value.length > 10 ? value : ''}
          followCursor
          arrow
        >
          <Box>
            <MediumTypography
              fontSize="16px"
              className="pl-xs"
              textColor="#2A4241"
              label={value || '-'}
              sxProps={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderAddressField = (address: string) => (
    <Box className="flex__ pr-md">
      <MediumTypography
        labelid={props.addressLabelId}
        defaultlabel={props.addressDefaultLabelId}
        fontSize="16px"
        textColor="#97A6A5"
      />
      <Box className="width300px">
        <Tooltip
          title={
            address && address.length > 10 ? address.replace(/,/g, ', ') : ''
          }
          followCursor
          arrow
        >
          <Box>
            <MediumTypography
              fontSize="16px"
              className="pl-xs"
              textColor="#2A4241"
              label={address?.replace(/,/g, ', ') || '-'}
              sxProps={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderRegularContact = (details: ConsentClientContact) => {
    const labels = details.isGuardian
      ? [
          details.isPrimary && 'Primary',
          details.isLegalGuardian && 'Legal',
        ].filter(Boolean)
      : [];

    return (
      <Box className="pt-md" key={details.id}>
        <Box className="width200px">
          <Tooltip
            title={details.name && details.name.length > 5 ? details.name : ''}
            followCursor
            arrow
          >
            <Box>
              <MediumTypography
                fontSize="16px"
                textColor="#2A4241"
                label={`${details.name}${
                  labels.length > 0 && details.isGuardian
                    ? ` - (${labels.join(', ')})`
                    : ''
                }`}
                sxProps={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box className="flex__">
          {renderAddressField(details.address ?? '')}
          {renderContactField(
            props.emailLabelId,
            props.emailDefaultLabelId,
            details.email,
          )}
          {renderContactField(
            props.phoneLabelId,
            props.phoneDefaultLabelId,
            details.phone,
          )}
        </Box>
      </Box>
    );
  };

  const renderOtherContact = (details: ConsentClientContact) => (
    <Grid container className="pt-md" key={details.id}>
      <Grid item xs={4} className="multisearchHgt">
        <Textinput
          labelid="ConsentForms.otherContactLabel"
          defaultlabelid="Other Contact Name"
          Required={true}
          inputProps={{ maxLength: 50 }}
          handlechange={(value: string) => {
            const validatedValue = nameValidation(value);
            if (validatedValue !== undefined) {
              props.handleContactOtherName(validatedValue);
            }
          }}
          disabled={props.screenLocked}
          Value={props.contactOtherName}
        />
        {props.dropDownOtherContactValidationState && (
          <MediumTypography
            labelid={'Clientpage.immuneerror'}
            textColor="red"
            marginTop="8px"
          />
        )}
      </Grid>
    </Grid>
  );

  return (
    <Box className="width__100 pb-sm">
      <MediumTypography
        labelid={props.titleLabel}
        defaultlabel={props.defaultTitleLabel}
        fontSize={'18px'}
        fontweight={600}
        className="pt-sm pb-md"
      />
      <Grid container>
        <Grid item xs={4} className="multisearchHgt">
          <MultipleAutocompleteSearch
            data={props.contactData}
            labelId={props.searchContactLabel}
            defaultId={props.defaultSearchContactLabel}
            value={selectAgency}
            onSelectItem={(value) => handleDropdownChange(value)}
            required
            disabled={props.screenLocked}
          />
          {props.dropDownValidation && (
            <MediumTypography
              labelid={'Intake.EICSField.required'}
              textColor="red"
              marginTop="8px"
            />
          )}
        </Grid>
      </Grid>
      {selectedContact.length > 0 &&
        selectedContact.map((details) =>
          details.type !== 'Other'
            ? renderRegularContact(details)
            : details.type === 'Other' && renderOtherContact(details),
        )}
    </Box>
  );
};

export default ConsentSspAgencyDetails;
